import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Separator } from '@/components/ui/separator';
import { useSiteInfo } from '@/SiteInfo/hooks/use-site-info';

interface TermsOfServiceSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const TermsOfServiceSheet = ({ open, onOpenChange }: TermsOfServiceSheetProps) => {
  const { data: siteInfo } = useSiteInfo();
  const siteName = siteInfo?.SiteTitle ?? 'Store';
  const hasCoupons = siteInfo?.SiteHasCoupons ?? false;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-2xl p-0 flex flex-col">
        <SheetHeader className="px-6 pt-6 pb-4 border-b text-left">
          <SheetTitle>Terms of Service</SheetTitle>
          <SheetDescription>
            Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
          {/* Introduction */}
          <section>
            <h3 className="text-lg font-semibold mb-3">1. Acceptance of Terms</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Welcome to {siteName}. By accessing or using our website, mobile application, or any of our services
              (collectively, the "Services"), you agree to be bound by these Terms of Service ("Terms"). If you do not
              agree to these Terms, please do not use our Services.
            </p>
          </section>

          <Separator />

          {/* Services Description */}
          <section>
            <h3 className="text-lg font-semibold mb-3">2. Description of Services</h3>
            <p className="text-sm text-muted-foreground leading-relaxed mb-3">
              {siteName} provides the following services through our website and mobile application:
            </p>
            <ul className="text-sm text-muted-foreground leading-relaxed list-disc pl-5 space-y-2">
              <li><strong>Weekly Ads:</strong> Browse our current weekly advertisements and special offers</li>
              <li><strong>Store Locations:</strong> Find store locations, hours, and contact information</li>
              <li><strong>Recipes:</strong> Access our collection of recipes featuring products available at our stores</li>
              {hasCoupons && (
                <li><strong>Digital Coupons:</strong> Clip and save digital coupons to your account for use at checkout</li>
              )}
              <li><strong>Contact:</strong> Submit inquiries and feedback to our team</li>
            </ul>
          </section>

          <Separator />

          {/* Eligibility */}
          <section>
            <h3 className="text-lg font-semibold mb-3">3. Eligibility</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Our Services are available only to individuals who are 13 years of age or older. By using our Services,
              you represent and warrant that you meet this age requirement. If you are under 18 years of age, you may
              only use our Services with the involvement and consent of a parent or legal guardian.
            </p>
          </section>

          <Separator />

          {/* Account Registration */}
          {hasCoupons && (
            <>
              <section>
                <h3 className="text-lg font-semibold mb-3">4. Account Registration</h3>
                <p className="text-sm text-muted-foreground leading-relaxed mb-3">
                  To access certain features such as digital coupons, you may be required to create an account.
                  When registering, you agree to:
                </p>
                <ul className="text-sm text-muted-foreground leading-relaxed list-disc pl-5 space-y-2">
                  <li>Provide accurate, current, and complete information</li>
                  <li>Maintain and promptly update your account information</li>
                  <li>Keep your phone number and verification codes confidential</li>
                  <li>Accept responsibility for all activities that occur under your account</li>
                  <li>Notify us immediately of any unauthorized use of your account</li>
                </ul>
                <p className="text-sm text-muted-foreground leading-relaxed mt-3">
                  We authenticate accounts using phone number verification via SMS. Standard message and data rates
                  may apply. You may opt out of SMS communications at any time.
                </p>
              </section>

              <Separator />
            </>
          )}

          {/* Digital Coupons */}
          {hasCoupons && (
            <>
              <section>
                <h3 className="text-lg font-semibold mb-3">5. Digital Coupons</h3>
                <p className="text-sm text-muted-foreground leading-relaxed mb-3">
                  Our digital coupon program is subject to the following terms:
                </p>
                <ul className="text-sm text-muted-foreground leading-relaxed list-disc pl-5 space-y-2">
                  <li>Coupons must be clipped to your account before purchase</li>
                  <li>Each coupon may only be used once unless otherwise specified</li>
                  <li>Coupons cannot be combined with other offers unless stated</li>
                  <li>Coupons have expiration dates and are void after the stated date</li>
                  <li>We reserve the right to modify, limit, or discontinue any coupon offer</li>
                  <li>Coupons have no cash value and cannot be sold or transferred</li>
                  <li>Misuse of coupons may result in account suspension</li>
                </ul>
              </section>

              <Separator />
            </>
          )}

          {/* User Conduct */}
          <section>
            <h3 className="text-lg font-semibold mb-3">{hasCoupons ? '6' : '4'}. User Conduct</h3>
            <p className="text-sm text-muted-foreground leading-relaxed mb-3">
              When using our Services, you agree not to:
            </p>
            <ul className="text-sm text-muted-foreground leading-relaxed list-disc pl-5 space-y-2">
              <li>Violate any applicable laws or regulations</li>
              <li>Infringe upon the rights of others</li>
              <li>Submit false or misleading information</li>
              <li>Attempt to gain unauthorized access to our systems</li>
              <li>Interfere with or disrupt the Services</li>
              <li>Use automated means to access the Services without permission</li>
              <li>Engage in any fraudulent activity</li>
            </ul>
          </section>

          <Separator />

          {/* Intellectual Property */}
          <section>
            <h3 className="text-lg font-semibold mb-3">{hasCoupons ? '7' : '5'}. Intellectual Property</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              All content on our Services, including text, graphics, logos, images, recipes, and software, is the
              property of {siteName} or its licensors and is protected by copyright, trademark, and other intellectual
              property laws. You may not reproduce, distribute, modify, or create derivative works from any content
              without our express written permission.
            </p>
          </section>

          <Separator />

          {/* Disclaimers */}
          <section>
            <h3 className="text-lg font-semibold mb-3">{hasCoupons ? '8' : '6'}. Disclaimers</h3>
            <p className="text-sm text-muted-foreground leading-relaxed mb-3">
              THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS
              OR IMPLIED. WE DO NOT WARRANT THAT:
            </p>
            <ul className="text-sm text-muted-foreground leading-relaxed list-disc pl-5 space-y-2">
              <li>The Services will be uninterrupted or error-free</li>
              <li>Product availability, pricing, or promotional offers will be accurate at all times</li>
              <li>Recipe nutritional information is complete or accurate for your specific dietary needs</li>
              <li>Store hours and locations are always current</li>
            </ul>
          </section>

          <Separator />

          {/* Limitation of Liability */}
          <section>
            <h3 className="text-lg font-semibold mb-3">{hasCoupons ? '9' : '7'}. Limitation of Liability</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, {siteName.toUpperCase()} SHALL NOT BE LIABLE FOR ANY INDIRECT,
              INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING FROM YOUR USE OF THE SERVICES. OUR
              TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT YOU PAID TO US IN THE TWELVE (12) MONTHS PRECEDING THE
              CLAIM, OR ONE HUNDRED DOLLARS ($100), WHICHEVER IS LESS.
            </p>
          </section>

          <Separator />

          {/* Changes to Terms */}
          <section>
            <h3 className="text-lg font-semibold mb-3">{hasCoupons ? '10' : '8'}. Changes to Terms</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              We may update these Terms from time to time. We will notify you of any material changes by posting
              the new Terms on our website and updating the "Last updated" date. Your continued use of the Services
              after any changes constitutes your acceptance of the new Terms.
            </p>
          </section>

          <Separator />

          {/* Contact */}
          <section>
            <h3 className="text-lg font-semibold mb-3">{hasCoupons ? '11' : '9'}. Contact Us</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              If you have any questions about these Terms of Service, please contact us through our Contact page
              or visit one of our store locations.
            </p>
          </section>
        </div>
      </SheetContent>
    </Sheet>
  );
};
